/**
 * Boards index — Trello-like list of all boards visible to the current user.
 *
 * - Admins/Supervisors see the "+ Create Board" affordance with a visibility
 *   selector (Company or Personal).
 * - Staff see "+ Create Personal Board" only.
 * - The legacy Jobs Board still lives at /jobs/board and is linked from
 *   the top of this page so workflows aren't lost.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Plus, Trello, Search, Archive, Lock, Building, Loader2 } from 'lucide-react';

const API = `${window.__BACKEND_URL__}/api`;

export default function Boards() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const isPriv = user?.role === 'admin' || user?.role === 'supervisor';

  const fetchBoards = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API}/boards`, { headers: { Authorization: `Bearer ${token}` } });
      setBoards(r.data || []);
    } catch (err) {
      console.error('Load boards failed', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchBoards(); }, [fetchBoards]);

  const doSearch = async (q) => {
    setSearch(q);
    if (!q || q.trim().length < 2) { setSearchResults(null); return; }
    try {
      const r = await axios.get(`${API}/boards/search`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { q: q.trim() },
      });
      setSearchResults(r.data.results || []);
    } catch (err) {
      console.error('Search failed', err);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="boards-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Trello className="w-6 h-6" style={{ color: '#1E88E5' }} /> Boards
          </h1>
          <p className="text-sm mt-1" style={{ color: '#64748B' }}>
            Trello-style boards for any workflow. The Jobs Board (auto-synced with your job list) lives separately at{' '}
            <button onClick={() => navigate('/jobs/board')} className="underline" style={{ color: '#1E88E5' }}>/jobs/board</button>.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white"
          style={{ background: '#1E88E5' }}
          data-testid="create-board-btn"
        >
          <Plus className="w-4 h-4" /> Create Board
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#64748B' }} />
        <input
          value={search}
          onChange={(e) => doSearch(e.target.value)}
          placeholder="Search across all boards (e.g. '1655', 'tree trimming', 'BGIS')"
          className="w-full pl-9 pr-3 py-2 rounded-lg text-sm text-white"
          style={{ background: '#1A2433', border: '1px solid #2D3748' }}
          data-testid="board-search-input"
        />
      </div>

      {/* Search results (if any) */}
      {searchResults && (
        <div className="ht-card p-4" data-testid="board-search-results">
          <p className="text-xs uppercase tracking-wider mb-2" style={{ color: '#64748B' }}>
            {searchResults.length} card{searchResults.length === 1 ? '' : 's'} found
          </p>
          {searchResults.length === 0 ? (
            <p className="text-sm" style={{ color: '#64748B' }}>No matches.</p>
          ) : (
            <ul className="space-y-2">
              {searchResults.map((c) => (
                <li
                  key={c.id}
                  onClick={() => navigate(`/boards/${c.board_id}?card=${c.id}`)}
                  className="cursor-pointer p-2 rounded-lg hover:bg-white/5 flex items-center justify-between"
                  data-testid={`search-result-${c.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white truncate font-medium">{c.title}</p>
                    <p className="text-xs" style={{ color: '#64748B' }}>
                      {c.board_name || '—'}
                      {c.due_date ? ` • due ${c.due_date}` : ''}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Grid of boards */}
      {loading ? (
        <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: '#1E88E5' }} /></div>
      ) : boards.length === 0 ? (
        <div className="ht-card p-10 text-center" style={{ color: '#64748B' }}>
          No boards yet. Click <b>Create Board</b> to start one.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="boards-grid">
          {boards.map((b) => (
            <button
              key={b.id}
              onClick={() => navigate(`/boards/${b.id}`)}
              className="ht-card p-4 text-left hover:border-blue-500/40 transition-all relative overflow-hidden"
              data-testid={`board-card-${b.id}`}
            >
              <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: b.color || '#1E88E5' }} />
              <div className="flex items-start justify-between mt-2">
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm font-semibold text-white truncate">{b.name}</h3>
                  {b.description && (
                    <p className="text-xs mt-1 line-clamp-2" style={{ color: '#A0AEC0' }}>{b.description}</p>
                  )}
                </div>
                <span
                  className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide flex items-center gap-1 flex-shrink-0"
                  style={{
                    background: b.visibility === 'personal' ? 'rgba(167,139,250,0.15)' : 'rgba(30,136,229,0.15)',
                    color: b.visibility === 'personal' ? '#A78BFA' : '#1E88E5',
                  }}
                >
                  {b.visibility === 'personal' ? <Lock className="w-3 h-3" /> : <Building className="w-3 h-3" />}
                  {b.visibility}
                </span>
              </div>
              <div className="mt-3 flex items-center gap-3 text-xs" style={{ color: '#64748B' }}>
                <span>{b.card_count ?? 0} cards</span>
                {b.archived && <span className="flex items-center gap-1"><Archive className="w-3 h-3" /> archived</span>}
              </div>
            </button>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateBoardModal
          isPriv={isPriv}
          onClose={() => setShowCreate(false)}
          onCreated={(b) => { setShowCreate(false); navigate(`/boards/${b.id}`); }}
        />
      )}
    </div>
  );
}

function CreateBoardModal({ isPriv, onClose, onCreated }) {
  const { token } = useAuth();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('#1E88E5');
  const [visibility, setVisibility] = useState(isPriv ? 'company' : 'personal');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const COLORS = ['#1E88E5', '#00C896', '#FFA000', '#EF4444', '#A78BFA', '#10B981'];

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr(null);
    try {
      const r = await axios.post(`${API}/boards`, {
        name, description, color, visibility,
      }, { headers: { Authorization: `Bearer ${token}` } });
      onCreated(r.data);
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Failed to create board');
    } finally { setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4"
      style={{ background: 'rgba(11,19,33,0.85)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
      data-testid="create-board-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-md rounded-2xl p-6"
        style={{ background: '#1A2433', border: '1px solid #2D3748' }}
      >
        <h2 className="text-lg font-semibold text-white mb-4">New Board</h2>
        <div className="space-y-3">
          <label className="block text-xs font-medium text-slate-400">
            Name
            <input
              autoFocus
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              className="mt-1 w-full px-3 py-2 rounded-lg text-sm text-white"
              style={{ background: '#0F1923', border: '1px solid #2D3748' }}
              data-testid="board-name-input"
            />
          </label>
          <label className="block text-xs font-medium text-slate-400">
            Description (optional)
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="mt-1 w-full px-3 py-2 rounded-lg text-sm text-white resize-none"
              style={{ background: '#0F1923', border: '1px solid #2D3748' }}
            />
          </label>
          <div>
            <p className="text-xs font-medium text-slate-400 mb-1">Color</p>
            <div className="flex gap-2">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  aria-label={`color ${c}`}
                  className="w-8 h-8 rounded-lg transition-transform"
                  style={{
                    background: c,
                    border: color === c ? '2px solid #fff' : '2px solid transparent',
                    transform: color === c ? 'scale(1.1)' : 'scale(1)',
                  }}
                />
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-medium text-slate-400 mb-1">Visibility</p>
            <div className="flex gap-2">
              {isPriv && (
                <button
                  type="button"
                  onClick={() => setVisibility('company')}
                  className="flex-1 py-2 rounded-lg text-xs font-medium flex items-center justify-center gap-1.5"
                  style={{
                    background: visibility === 'company' ? '#1E88E5' : 'transparent',
                    color: visibility === 'company' ? '#fff' : '#A0AEC0',
                    border: `1px solid ${visibility === 'company' ? '#1E88E5' : '#2D3748'}`,
                  }}
                  data-testid="visibility-company"
                >
                  <Building className="w-3.5 h-3.5" /> Company
                </button>
              )}
              <button
                type="button"
                onClick={() => setVisibility('personal')}
                className="flex-1 py-2 rounded-lg text-xs font-medium flex items-center justify-center gap-1.5"
                style={{
                  background: visibility === 'personal' ? '#A78BFA' : 'transparent',
                  color: visibility === 'personal' ? '#0B1321' : '#A0AEC0',
                  border: `1px solid ${visibility === 'personal' ? '#A78BFA' : '#2D3748'}`,
                }}
                data-testid="visibility-personal"
              >
                <Lock className="w-3.5 h-3.5" /> Personal
              </button>
            </div>
          </div>
        </div>
        {err && <p className="text-xs mt-3" style={{ color: '#EF4444' }}>{err}</p>}
        <div className="mt-5 flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm" style={{ color: '#A0AEC0', border: '1px solid #2D3748' }}>
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !name.trim()}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-50"
            style={{ background: '#1E88E5' }}
            data-testid="board-create-submit"
          >
            {saving ? 'Creating…' : 'Create'}
          </button>
        </div>
      </form>
    </div>
  );
}
